/**
 * The hub scene — the town (docs/design/hub.md). A pure presentation layer
 * over the hub roster: a big flat plane, your hero in the account's cosmetic
 * look, the other players standing around, and the dungeon door to the north.
 *
 * The scene feeds a SYNTHESIZED Observation into the shared Viewport (the
 * same renderer, interpolator and input model as the floor — AGENTS §6's
 * one implementation), so hero composites, name tags, and account looks
 * can't drift between the town and a floor. The synthesis is the hub's
 * whole trick, and it keeps the hub honest:
 *   - the plane is a shared PRESENTATION constant (HUB_PLANE_W/H) — the
 *     server clamps what it relays to it, but there is still no server map,
 *     no generation, no fog, no hash stream;
 *   - positions are cosmetic and SELF-REPORTED: each client walks its own
 *     hero and reports the tile it reached (ClientOp.HubMove); the hub
 *     relays, it never moves anyone, so there is still nothing to decide and
 *     no second sim (hub.md);
 *   - there is no tick: the hub has no sim, so the local counter here only
 *     advances the render clock. Nothing the hub shows can affect a run.
 *
 * Walking (2026-09-16): YOUR hero walks where you click, on the same 1-tile
 * steps and the same interpolation as a floor — and the rest of the town sees
 * it. Each step is reported; the hub rebroadcasts the roster; the other
 * clients feed those tiles to the SAME interpolator a floor uses, so a remote
 * hero glides and plays its walk cycle instead of hopping tile to tile. Your
 * own tile stays authoritative to YOU (the echo is ignored): a round trip must
 * never rubber-band your hero, for the same reason a floor client never accepts
 * its own position back. The report carries a tile and nothing else — no
 * collision, no ordering, no state a run can read.
 *
 * Parties (2026-09-16, hub.md): the town's party structure rides the SAME
 * party-uniform roster broadcast (HubState.parties/.invites, and the hub
 * Welcome) — this scene is a pure projection of it: the host's small marker
 * (an overlay pass, not a crown), the Group up / Leave party verbs, the
 * accept/decline strip, and the host-gated door. The server is the judge
 * (cap, accounts, host rule); the scene only sends the verbs and renders
 * the answer.
 */
import {
  CLASS_CATALOG,
  deriveGearCombatStats,
  CLASS_MAX_LEVEL,
  CLASS_MIN_LEVEL,
  CLASSES,
  COMBAT,
  EQUIP_SLOTS,
  FLOORS,
  SLOT_LABELS,
  TERRAIN_POND,
  TICK_MS,
  ITEMS,
  parseAppearance,
} from "@pd/shared";
import type {
  EquipSlot,
  PlayerCardWire,
  HeroAppearance,
  HubNpcWire,
  HubMemberInfo,
  InviteWire,
  Observation,
  ObservedPlayer,
  ObservedEnemy,
  PartyMemberInfo,
  PartyWire,
  PlaytestLoadoutWire,
  PlaytestCatalogWire,
  RaceWire,
} from "@pd/shared";
import { playerColor } from "./actions.js";
import type { PrimaryAction, Vec2 } from "./actions.js";
import { ChatPanel } from "./chat.js";
import { clearBubbles, drawChatBubbles, noteChat } from "./chatBubbles.js";
import type { ChatBubbleStore } from "./chatBubbles.js";
import { Hud, hubAbilities } from "./hud.js";
import type { RaceRowInfo } from "./hud.js";
import { mmss, raceHasClock, raceMySide, raceRemaining, raceRowInfo, raceWaitsOnMe } from "./raceRow.js";
import {
  BRAZIER_TILES,
  CROWD_SLOTS,
  DOOR_ANCHOR,
  DOOR_ROW,
  DOOR_W,
  SEAL_TILE,
  TOWN_FLOOR_ARCHITECTURE,
  TOWN_FLOOR_PALETTE,
  TOWN_FLOOR_WEAR,
  TOWN_PLANE_H,
  TOWN_PLANE_W,
  TOWN_SURFACE,
  drawBrazier,
  drawPlazaSeal,
  drawTownDoor,
} from "./townScene.js";
import type { CtxItem, CtxMenu } from "./context.js";
import { buildRatingPlate, openPlayerCard } from "./playerCard.js";
import type { HeroAssets } from "./hero.js";
import { itemFrameUrl, loadHeroAssets } from "./hero.js";
import { packHeroLut } from "./heroLut.js";
import { HALF_H, tileToScreen } from "./iso.js";
import { enemyKey, playerKey } from "./interpolate.js";
import { enemyCandidatesAtScreenPoint, enemyHitsAtScreenPoint, playerCandidatesAtScreenPoint, playerHitsAtScreenPoint } from "./hitbox.js";
import { gearStatsPanel, itemEfficiency, itemStatsPanel } from "./gear-stats.js";
import { createGameModal } from "./modal.js";
import type { GameModal } from "./modal.js";
import type { PlayerLook, WorldProp } from "./render.js";
import { NetClient } from "./net.js";
import type { SessionInfo } from "./net.js";
import { Viewport } from "./viewport.js";
import type { OverlayApi, ViewportDebug } from "./viewport.js";
import { getAuthUsername } from "./auth.js";

// ---- the town (client-side presentation constants — never on the wire) ----

/** The town's geometry — plane, door, crowd band, authored surface — lives in
 *  `townScene.ts`, because the hub and the sign-in scene draw the SAME
 *  square: one geometry, one door, no drift between the two surfaces (the
 *  login page used to carry its own CSS copy). The plane SIZE still lives in
 *  shared (HUB_PLANE_W/H) because the server bounds the positions it relays
 *  with it: drawn plane and validated plane must be one number, or a client
 *  can report a tile nobody can ever see. */
const PLANE_W = TOWN_PLANE_W;
const PLANE_H = TOWN_PLANE_H;

/** SPAWN slots: where a presence stands until their first position report
 *  (allocated below, never by id arithmetic). The band itself is town
 *  geometry — see `townScene.ts` CROWD_SLOTS. */
const SLOTS = CROWD_SLOTS;

/** The walk clock's cadence: the hero's own floor pace (combat.store), so
 *  walking in town feels exactly like walking on a floor. The interpolator
 *  gets the same number through obs.combat.moveCostTicks, which is what
 *  makes the steps join into one continuous walk. */
const WALK_MS = COMBAT.moveCostTicks * TICK_MS;

/** Synthetic observation id for the hub-only Bull. Player ids are
 *  non-negative, so the negative id cannot collide with a real presence. */
const BULL_ID = -2;
const BULL_MOVE_TICKS = 44; // 2,200 ms / TICK_MS — presentation cadence only

/** The town's authored presentation surface (pools, stone seats) — town
 *  geometry, shared with the sign-in scene. Obstacles are real renderer
 *  terrain, not UI decoration, and local cosmetic routing treats them as
 *  blocked. */

/**
 * A presence as the TOWN renders it: identity plus the tile they reported
 * (`ClientOp.HubMove`), or `pos: null` when nothing usable arrived — then
 * their spawn slot stands in (see `posFor`).
 *
 * Both wire sources pass through `toPresence`: the HubState packet (typed
 * `HubMemberInfo`, positions guaranteed) and the hub Welcome's `party` (typed
 * `PartyMemberInfo` — the same list, with positions in practice but not in the
 * type, because that field is shared with floor welcomes). One shape, one
 * place that decides whether a reported tile is usable.
 */
interface TownPresence {
  id: number;
  name: string;
  /** Raw wire look, parsed into `looks` on arrival (see `applyRoster`). */
  appearance?: HeroAppearance;
  pos: Vec2 | null;
}

/** A whole number or null (Number.isInteger is not a type guard, so the
 *  narrowing has to live in a helper). */
function asTile(v: unknown): number | null {
  return typeof v === "number" && Number.isInteger(v) ? v : null;
}

function toPresence(m: PartyMemberInfo): TownPresence {
  const wire = m as PartyMemberInfo & Partial<HubMemberInfo>;
  const x = asTile(wire.x);
  const y = asTile(wire.y);
  return {
    id: m.id,
    name: m.name,
    ...(m.appearance === undefined ? {} : { appearance: m.appearance }),
    pos: x !== null && y !== null ? { x, y } : null,
  };
}

export interface HubCallbacks {
  /** Sign out (clears the token, closes the socket, back to auth). */
  onSignOut(): void;
}

export class Hub {
  private net: NetClient;
  private callbacks: HubCallbacks;
  private info: SessionInfo | null = null;
  private roster: TownPresence[] = [];
  private npcs: HubNpcWire[] = [];
  private playtestLoadout: PlaytestLoadoutWire | null = null;
  private playtestCatalog: PlaytestCatalogWire | null = null;
  /** Party looks: memberId → packed look (the same parse + pack as the
   *  floor's Game). A member without an account is simply absent from the
   *  map (the renderer keeps the identity tint). */
  private looks = new Map<number, PlayerLook>();
  /** A local render counter for the synthesized obs — the hub has no sim
   *  tick; this only feeds the interpolation's clock and decides nothing. */
  private tick = 0;

  /** The town's party structure (the hub wire — see PartyWire/InviteWire):
   *  every party with ≥1 member in the town (a party of one IS a party —
   *  keyracing.md A1) + pending invites, in hub member ids. From the welcome
   *  and every HubState — one party-uniform source, projected into the
   *  marker, the party line, the invite window, and the door's gating. */
  private parties: PartyWire[] = [];
  private invites: InviteWire[] = [];
  /** The hero sprite atlas (the party marker reads the head anchor from it
   *  — players draw nothing before it arrives, and neither does the
   *  marker). */
  private heroAssets: HeroAssets | null = null;

  /** Where MY hero stands (walked, tile coords). Client-side only — it is
   *  never sent anywhere, which is what keeps the hub out of the sim. Null
   *  until the first Welcome allocates a spawn slot. */
  private mePos: Vec2 | null = null;
  /** The walk's standing order (a walk, like the floor's, keeps going until
   *  it arrives or something replaces it). */
  private walkTo: Vec2 | null = null;
  /** Remaining cosmetic route, excluding the tile currently occupied. */
  private walkPath: Vec2[] = [];
  private walkTimer: number | null = null;

  /** memberId → town slot. Allocated on first sight (lowest free index),
   *  freed on leave. NOT `id % slotCount`: hub ids are monotonic and never
   *  reused, so past 32 presences two members would share a tile (the
   *  id-collision trap class); and NOT roster position, which would teleport
   *  the whole crowd one slot whenever someone walked off. */
  private slotOf = new Map<number, number>();
  private slotTaken = new Set<number>();

  /** The plane's static layers, built once — the town never changes, so the
   *  per-step re-synthesis only rebuilds the player list. */
  private readonly planeMap = TOWN_SURFACE.map;
  private readonly planeRevealed = new Array<number>(PLANE_W * PLANE_H).fill(1); // no fog: the whole town, forever
  private readonly planeTerrain = TOWN_SURFACE.terrain;

  /** Latest visible line per speaker. The chat panel remains the transcript;
   *  this map is only the short-lived, in-world social cue (chatBubbles.ts,
   *  shared with the floor). */
  private readonly chatBubbles: ChatBubbleStore = new Map();

  private root: HTMLElement;
  private dom!: HTMLDivElement;
  /** The town's chat (hub.md — server-broadcast, no sim): the same panel
   *  the floor uses, mounted on the hub scene. */
  private chat!: ChatPanel;
  /** The same persistent workspace chrome used by a floor. The hub feeds it
   * hub projections; it does not own a separate lobby interface. */
  private hud!: Hud;
  private canvas!: HTMLCanvasElement;
  private viewport!: Viewport;
  private countEl!: HTMLSpanElement;
  /** The door confirm (hub.md's "Enter the dungeon" modal): floor type
   *  (floors.store's FLOORS) + size + Enter. Built on each open so the shared
   *  modal owns focus, Escape and backdrop dismissal. */
  private doorModal: GameModal | null = null;
  private doorFloorSel: HTMLSelectElement | null = null;
  private doorSizeSel: HTMLSelectElement | null = null;
  private doorEnterBtn: HTMLButtonElement | null = null;
  /** Whether the open door confirm was built for a knocker WITH a party
   *  (false = the "create a party first" face). The door requires a party
   *  (keyracing.md A1), so creating one while the confirm is open must
   *  re-render it into the real picker — this is the state it was built for,
   *  compared against myParty() on every roster update. */
  private doorNeedsParty: boolean | null = null;
  /** Which face the open door confirm was built for, so a roster or race update
   *  re-renders it into the right one (`doorFace()`). The party case is
   *  `doorNeedsParty`; this adds the third face a race brings. */
  private doorFaceNow: "none" | "needs-party" | "picker" | "race" = "none";
  /** The Bull's records dialogue and account build editor. */
  private bullModal: GameModal | null = null;
  /** The top bar's party line (who is grouped, who holds the door). */
  private partyEl!: HTMLSpanElement;
  /** The invite WINDOW (keyracing.md A3): a centred accept/reject modal for
   *  the pending invites addressed to me, replacing the old top-bar strip
   *  (which read as a caption, not a decision). Rebuilt ONLY when the invite
   *  set's signature changes — a roster update (a walk report, ~10/s in a
   *  busy town) landing between mousedown and mouseup would eat the click
   *  (traps/dom-rebuild.md). */
  private inviteModal: GameModal | null = null;
  private inviteSig = "";

  /**
   * The town's races (keyracing.md C1/D2): the last `RaceState`, plus when it
   * arrived. The packet carries REMAINING durations, so a countdown is anchored
   * to the moment it was received and runs down locally — presentation only,
   * exactly like the floor's interpolated movement: the client renders a clock,
   * it never decides one.
   */
  private races: RaceWire[] = [];
  /** The race state itself lives on the transport (`net.raceState`) so the row
   *  cannot blank across a scene switch; the arrival time its countdowns anchor to
   *  is shared the same way (`net.raceArrivedAt`). */
  private get raceSeenAt(): number {
    return this.net.raceArrivedAt;
  }
  /** The consent WINDOW: the one race surface that is a modal, and only for an
   *  OPEN challenge. Armed and racing have no windows — the party panel's row
   *  carries them (11-15), because "armed" is a registry state and a modal that
   *  explains it teaches the player the server's vocabulary. */
  private raceModal: GameModal | null = null;
  private raceSig = "";
  private raceClockTimer: number | null = null;
  /** Which challenge this window is showing, when several name my party at once
   *  (an account may be named by any number of open offers — only arming claims a
   *  slot). One window, stepped; a stack of modals is unusable and hiding the
   *  others would hide a vote. */
  private raceShown = 0;
  /**
   * Player cards, by HUB member id (keyracing.md D1b). A cache, not a store: it
   * exists so a menu opens with its final height instead of growing one, and so
   * re-opening one is instant. Cleared whenever the town's races move (a verdict
   * is exactly the event that makes a cached Elo wrong) and whenever the roster
   * renumbers (hub ids are re-issued on reconnect — traps/id-collision.md).
   */
  private cards = new Map<number, PlayerCardWire>();
  /** Which ids have been asked about, so a menu opened ten times asks once. A
   *  card that never arrives (the player left town) leaves the plate reading
   *  "rating…", which is the truth and is never a number pretending to be one. */
  private cardAsked = new Set<number>();
  /** The plate the open menu is showing, so a card that lands while it is up can
   *  fill in PLACE. Re-opening the menu to show an answer would rebuild the DOM
   *  under the pointer (traps/dom-rebuild.md) and dismiss a menu the player is
   *  still reading. */
  private openPlate: HTMLElement | null = null;
  private openPlateFor: number | null = null;
  /** The Examine that outran its own card: the id to open when it lands. */
  private cardOpenWanted: number | null = null;
  private cardModal: GameModal | null = null;
  /** The terms picker a HOST issues a challenge from (the door's sibling: floor
   *  type + size + rated, and no seed — the server draws that at arm). */
  private termsModal: GameModal | null = null;

  /** Dev/CDP introspection (the game's window.__game pattern) — the
   *  browser probe reads the roster + rendered positions from here. */
  private debug: ViewportDebug & {
    obs: Observation | null;
    info: SessionInfo | null;
    roster: TownPresence[];
    parties: PartyWire[];
    invites: InviteWire[];
    viewport: Viewport | null;
    camera: { camX: number; camY: number; isoUnit: number } | null;
    me: Vec2 | null;
    walkTarget: Vec2 | null;
    bubbles: Map<number, { text: string; until: number }>;
  } = {
    obs: null,
    info: null,
    roster: [],
    parties: [],
    invites: [],
    viewport: null,
    get camera() {
      return this.viewport ? this.viewport.camera : null;
    },
    me: null,
    walkTarget: null,
    bubbles: this.chatBubbles,
    positions: new Map(),
    anims: new Map(),
    markers: [],
  };

  constructor(root: HTMLElement, net: NetClient, callbacks: HubCallbacks) {
    this.root = root;
    this.net = net;
    this.callbacks = callbacks;
    this.dom = this.buildDom();
    this.viewport = new Viewport(
      this.canvas,
      this.dom,
      {
        meId: () => this.info?.playerId ?? null,
        primaryAction: (_obs, _me, x, y) => this.primaryAction(x, y),
        onPrimaryAction: (a) => this.onPrimaryAction(a),
        pointTile: (obs, _me, point, positions) => {
          const enemy = enemyHitsAtScreenPoint(
            this.heroAssets,
            obs,
            positions,
            point.sx,
            point.sy,
            point.isoUnit,
            point.camX,
            point.camY,
          )[0];
          if (enemy?.id === BULL_ID) return { x: enemy.x, y: enemy.y };
          const hits = playerHitsAtScreenPoint(
            this.heroAssets,
            obs,
            positions,
            point.sx,
            point.sy,
            point.isoUnit,
            point.camX,
            point.camY,
            _me.id,
          );
          const hit = hits[0];
          return hit ? { x: hit.x, y: hit.y } : null;
        },
        pointAction: (obs, _me, point, positions) => {
          const enemy = enemyHitsAtScreenPoint(
            this.heroAssets,
            obs,
            positions,
            point.sx,
            point.sy,
            point.isoUnit,
            point.camX,
            point.camY,
          )[0];
          if (enemy?.id !== BULL_ID) return null;
          return {
            kind: "interact",
            label: "Talk to The Bull",
            obj: { kind: "boss", id: BULL_ID, x: enemy.x, y: enemy.y, state: "portal" },
          };
        },
        contextItemsAtPoint: (obs, _me, point, positions) => {
          const enemyHits = enemyHitsAtScreenPoint(
            this.heroAssets,
            obs,
            positions,
            point.sx,
            point.sy,
            point.isoUnit,
            point.camX,
            point.camY,
          );
          const enemyCandidates = enemyCandidatesAtScreenPoint(
            this.heroAssets,
            obs,
            positions,
            point.sx,
            point.sy,
            point.isoUnit,
            point.camX,
            point.camY,
          );
          const bull = (enemyHits.length > 0 ? enemyHits : enemyCandidates).find((e) => e.id === BULL_ID);
          if (bull) return this.contextItemsForBull(bull.x, bull.y);
          const hits = playerHitsAtScreenPoint(
            this.heroAssets,
            obs,
            positions,
            point.sx,
            point.sy,
            point.isoUnit,
            point.camX,
            point.camY,
            _me.id,
          );
          const candidates = playerCandidatesAtScreenPoint(
            this.heroAssets,
            obs,
            positions,
            point.sx,
            point.sy,
            point.isoUnit,
            point.camX,
            point.camY,
          );
          if (hits.length === 0) {
            if (candidates.length === 0 || this.isDoor(point.tile.x, point.tile.y)) return null;
            if (candidates.length === 1) {
              const dest = this.isWalkable(point.tile.x, point.tile.y) ? point.tile : null;
              return {
                title: "Ground",
                items: [{
                  label: "Walk here",
                  kind: "walk",
                  disabled: !dest,
                  run: dest ? () => this.walkHere(dest.x, dest.y) : undefined,
                }],
              };
            }
          }
          const targets = hits.length > 0 ? hits : candidates;
          const menuForTarget = (target: ObservedPlayer): { title: string; items: CtxItem[] } | null => {
            // Resolve by id, not by tile: two rendered bodies can share a
            // reported tile, and the old tile helper would collapse both to
            // whichever roster entry happened to be visited first.
            const member = this.roster.find((m) => m.id === target.id);
            return member ? this.contextItemsForPlayer(member, target.x, target.y) : this.contextItems(target.x, target.y);
          };
          if (targets.length === 1) return menuForTarget(targets[0]!);
          const items: CtxItem[] = [];
          for (const hit of targets) {
            const menu = menuForTarget(hit);
            if (!menu) continue;
            items.push(...menu.items.filter((item) => item.label !== "Walk here"));
          }
          if (this.isWalkable(point.tile.x, point.tile.y)) {
            items.push({ label: "Walk here", kind: "walk", run: () => this.walkHere(point.tile.x, point.tile.y) });
          }
          return { title: "Choose option", items };
        },
        contextItems: (_obs, _me, x, y) => this.contextItems(x, y),
        nameFor: (id) => this.nameOf(id),
        lookFor: (id) => this.looks.get(id) ?? null,
        overlay: (o) => this.overlay(o),
        onKey: (ev) => {
          if (ev.type !== "keydown" || ev.repeat || ev.key !== "Enter" || ev.ctrlKey || ev.metaKey || ev.altKey) return;
          const target = ev.target;
          if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return;
          if (target instanceof HTMLElement && (target.isContentEditable || target.closest('button, a, [role="button"], [role="dialog"]'))) return;
          ev.preventDefault();
          this.chat.focusInput();
        },
      },
      this.debug,
    );
    this.debug.viewport = this.viewport;
    // @ts-expect-error — deliberate global for tooling
    window.__hub = this.debug;
    // The hub and dungeon share one civic material language. Keep wear low:
    // this is a maintained public square, not another generated room.
    this.viewport.setFloorLook(TOWN_FLOOR_PALETTE, TOWN_FLOOR_ARCHITECTURE, TOWN_FLOOR_WEAR);
    // The dungeon door joins the painter pass (not the overlay): a hero that
    // walks in front of it must draw OVER it, and one behind it UNDER.
    const doorProp: WorldProp = {
      x: DOOR_ANCHOR.x,
      y: DOOR_ANCHOR.y,
      draw: (ctx, u, sx0, sy0) => drawTownDoor(ctx, u, sx0, sy0),
    };
    const props: WorldProp[] = [
      doorProp,
      // Two warm points frame the route without turning the plaza into a
      // prop field. Their row is immediately south of the threshold, so the
      // painter pass handles heroes crossing in front of them correctly.
      ...BRAZIER_TILES.map((t) => ({ x: t.x, y: t.y, draw: (ctx: CanvasRenderingContext2D, u: number, sx0: number, sy0: number) => drawBrazier(ctx, u, sx0, sy0) })),
      // A small civic seal gives the otherwise endless grid a visual centre
      // and makes the crowd band read as a plaza rather than a test plane.
      { x: SEAL_TILE.x, y: SEAL_TILE.y, draw: (ctx, u, sx0, sy0) => drawPlazaSeal(ctx, u, sx0, sy0) },
    ];
    this.viewport.setWorldProps(props);
    // The sprite atlas (players draw nothing until it arrives, exactly
    // like the floor — the floor tiles keep the flat diamond fallback; the
    // party marker waits for it too — the head anchor comes from it).
    void loadHeroAssets().then((a) => {
      if (!a) {
        this.hud.showBanner("Game art could not be loaded. Reload to retry.");
        return;
      }
      this.heroAssets = a;
      this.viewport.setHeroAssets(a);
    });
  }

  // ---- public hooks (called by index.ts routing) -------------------------

  onWelcome(info: SessionInfo): void {
    // A new presence (first login, or a re-join after a drop — the hub's ids
    // are monotonic and never reused): the old identity's slot and walked
    // position are meaningless, so start fresh at the town.
    if (this.info?.playerId !== info.playerId) {
      this.mePos = null;
      this.slotOf.clear();
      this.slotTaken.clear();
    }
    this.stopWalk();
    this.info = info;
    this.debug.info = info;
    this.npcs = info.npcs ?? [];
    this.playtestLoadout = info.playtestLoadout ?? null;
    this.playtestCatalog = info.playtestCatalog ?? null;
    this.hud.renderAbilities(hubAbilities(this.playtestLoadout?.classKind));
    // The hub Welcome carries the town's party structure (a floor Welcome
    // does not — and this scene only ever receives hub welcomes here).
    this.parties = info.parties ?? [];
    this.invites = info.invites ?? [];
    this.debug.parties = this.parties;
    this.debug.invites = this.invites;
    // The town's races come from the transport's cache (net.ts), which this very
    // Welcome has just refreshed: a player who walks in late must see the challenge
    // the town is already holding up, or unanimity reads to them as a bug — and a
    // player coming home from a floor must not be handed the snapshot written
    // BEFORE their own side's forfeit landed, which is what the Welcome packet is.
    this.races = [...this.net.raceState];
    this.applyRoster(info.party);
    this.updateRaceSurfaces();
  }

  /**
   * The race state (ServerOp.RaceState — keyracing.md C1/D2). The packet IS the
   * state: a race missing from it is a race that died (revoked, expired, or
   * resolved and dropped from the registry), so this replaces the list wholesale
   * rather than merging — merging would keep a window the server already closed.
   */
  onRaceState(p: { races: RaceWire[] }): void {
    // A verdict moves ratings, and this is the packet that says one happened.
    // Cards are re-read on the next menu open rather than tracked per race.
    this.cards.clear();
    this.cardAsked.clear();
    const before = new Set(this.races.map((r) => r.id));
    this.races = p.races;
    // A challenge that just arrived is announced in chat: the window is the
    // decision surface, but a party in another room needs to hear that they are
    // being asked for something before they can decide to look.
    for (const race of this.races) {
      if (race.state === "open" && !before.has(race.id) && this.namesMyParty(race)) {
        this.chat.system(`${this.nameOf(race.from)}'s party challenges your party to a race.`);
      }
    }
    this.updateRaceSurfaces();
  }

  /** One side forfeited (ServerOp.RaceForfeit, C3). Server→client only, and
   *  delivered to the side it happened to wherever they are. The word is display
   *  — the rating scores every one of them the same — but a player told they
   *  forfeited with no reason reads it as a bug. */
  onRaceForfeit(p: { race: number; side: 0 | 1; reason: string }): void {
    const race = this.races.find((r) => r.id === p.race);
    const who = race && this.isMine(race, p.side) ? "Your side" : "The other side";
    const why: Record<string, string> = {
      retreated: "the boss was still alive",
      incomplete: "the floor was not fully mapped",
      abandoned: "nobody was there when it ended",
      no_show: "the door was never opened",
    };
    this.chat.system(`Race over for ${who.toLowerCase()} — ${why[p.reason] ?? p.reason}.`);
    this.updateRaceSurfaces();
  }

  /**
   * A player card landed (ServerOp.PlayerCard — keyracing.md D1b). Three things
   * may have happened to the thing that asked, and all three are normal: the menu
   * closed, the player left town and their hub id was re-issued to somebody else
   * (traps/id-collision.md), or an Examine is still waiting on this very card.
   * So: cache it, fill a plate ONLY if that menu is still open and still about
   * this id, and open a waiting Examine. Never re-open a menu to show an answer.
   */
  onPlayerCard(p: { card: PlayerCardWire }): void {
    const card = p.card;
    this.cards.set(card.id, card);
    if (this.openPlateFor === card.id && this.openPlate?.isConnected) {
      const next = buildRatingPlate(card);
      this.openPlate.replaceWith(next);
      this.openPlate = next;
    }
    if (this.cardOpenWanted === card.id) {
      this.cardOpenWanted = null;
      this.showCard(card);
    }
  }

  /** The menu's rating plate: drawn from the cache the instant the menu opens (so
   *  the menu has its final height before the answer exists), with the request
   *  fired alongside it. One request per player per town visit. */
  private plateFor(id: number): HTMLElement {
    if (!this.cards.has(id) && !this.cardAsked.has(id)) {
      this.cardAsked.add(id);
      this.net.inspectPlayer(id);
    }
    const plate = buildRatingPlate(this.cards.get(id));
    this.openPlate = plate;
    this.openPlateFor = id;
    return plate;
  }

  /** Examine a player: their card, not a line of chat. The menu asked when it
   *  opened, so the cache is normally warm; when it is not, the card opens the
   *  moment it lands rather than the click doing nothing (traps/listener-noop.md
   *  — a verb that silently no-ops is worse than one that is missing). */
  private examinePlayer(id: number): void {
    const card = this.cards.get(id);
    if (card) {
      this.showCard(card);
      return;
    }
    this.cardOpenWanted = id;
    this.net.inspectPlayer(id);
  }

  private showCard(card: PlayerCardWire): void {
    this.closeCard();
    this.viewport.closeMenu();
    this.cardModal = openPlayerCard(this.dom, card, {
      assets: this.heroAssets,
      // The server's live store when the hub holds one (dev), the committed
      // catalog otherwise — the same source the Bull's editor names gear from,
      // so a card and the Bull cannot call one item two things.
      items: this.playtestCatalog?.items ?? ITEMS,
    });
  }

  private closeCard(): void {
    this.cardModal?.close();
    this.cardModal = null;
  }

  /** A line of town chat (ServerOp.HubChat): the sender's hub id resolves
   *  against the CURRENT roster — a stale id (the sender left between the
   *  broadcast and this render) falls back to "?" via nameOf, and the line
   *  still lands. The identity colour is the floor's playerColor (one hue
   *  per id, town and floor alike). */
  onHubChat(p: { from: number; message: string }): void {
    this.chat.party(this.nameOf(p.from), p.message, playerColor(p.from));
    noteChat(this.chatBubbles, p.from, p.message);
  }

  onHubState(p: { players: HubMemberInfo[]; parties: PartyWire[]; invites: InviteWire[]; npcs?: HubNpcWire[] }): void {
    this.parties = p.parties;
    this.invites = p.invites;
    this.debug.parties = this.parties;
    this.debug.invites = this.invites;
    this.npcs = p.npcs ?? this.npcs;
    this.applyRoster(p.players);
  }

  /** The Bull echoed the account build after a save. */
  onPlaytestLoadout(p: { loadout: PlaytestLoadoutWire }): void {
    this.playtestLoadout = p.loadout;
    this.hud.renderAbilities(hubAbilities(p.loadout.classKind));
    this.closeBullPanel();
    this.chat.system("The Bull has your next build ready.");
  }

  onError(reason: string): void {
    this.chat.invalid(reason);
    // A refused door (the only error the hub phase can send while a player
    // is here): the knock bounced, so the confirm goes away — and if the
    // refusal was terminal (unknown floor type, the server also closed),
    // the reconnect fallback lands back in the town and onWelcome clears.
    this.closeDoorPanel();
  }

  onClosed(): void {
    this.stopWalk();
    this.chat.system("Connection lost. Reconnecting…");
  }

  destroy(): void {
    this.stopWalk();
    this.closeDoorPanel();
    this.closeRaceWindow();
    this.closeTermsPanel();
    this.hud.renderRaceRow(null);
    this.doorFloorSel = null;
    this.doorSizeSel = null;
    this.doorEnterBtn = null;
    this.closeBullPanel();
    this.closeInviteWindow();
    this.stopRaceClock();
    this.partyEl = null as unknown as HTMLSpanElement;
    this.parties = [];
    this.invites = [];
    this.races = [];
    this.npcs = [];
    this.playtestLoadout = null;
    this.playtestCatalog = null;
    clearBubbles(this.chatBubbles);
    this.closeCard();
    this.cards.clear();
    this.cardAsked.clear();
    this.openPlate = null;
    this.openPlateFor = null;
    this.heroAssets = null;
    this.viewport.destroy();
    this.root.textContent = "";
    // @ts-expect-error — deliberate global for tooling
    if (window.__hub === this.debug) delete window.__hub;
  }

  // ---- the town feed ------------------------------------------------------

  private applyRoster(players: readonly PartyMemberInfo[]): void {
    const list = players.map(toPresence);
    // Hub ids are re-issued on every reconnect, so a roster change can silently
    // re-point a cached card at a different person (traps/id-collision.md). Drop
    // anything the town no longer holds rather than risk showing Ada's Elo under
    // Bram's name.
    if (this.cards.size > 0) {
      const here = new Set(list.map((p) => p.id));
      for (const id of [...this.cards.keys()]) if (!here.has(id)) this.cards.delete(id);
      for (const id of [...this.cardAsked]) if (!here.has(id)) this.cardAsked.delete(id);
    }
    this.roster = list;
    this.debug.roster = list;
    const next = new Map<number, PlayerLook>();
    for (const m of list) {
      const app = m.appearance === undefined ? null : parseAppearance(m.appearance);
      if (!app) continue;
      // An all-canonical colour look packs to a null LUT — the member still
      // wears the CANON hero (their saved outfit), not the identity tint.
      next.set(m.id, { lut: packHeroLut(app.colours), outfit: app.outfit });
    }
    this.looks = next;
    this.assignSlots(list);
    // My spawn: my allocated slot, once — announced immediately, because the
    // town only knows what I report and the server's default is the plane's
    // centre. After that the walked position owns my tile, so a roster change
    // never yanks me back across the square.
    if (!this.mePos && this.info) {
      this.mePos = { ...this.slotFor(this.info.playerId) };
      this.sendPos();
    }
    this.countEl.textContent = `${players.length} at the hub`;
    this.updatePartyLine();
    this.updateInviteWindow();
    this.refreshDoorPanel();
    this.pushObs();
  }

  // ---- the party (a projection of the hub wire — the server is the judge) ----

  /** My party (null = you are in NO party — keyracing.md A1: a party exists
   *  only once you created one or joined one, and a party of one IS a party,
   *  which is what the door requires). */
  private myParty(): PartyWire | null {
    const me = this.info?.playerId;
    if (me === undefined) return null;
    return this.parties.find((p) => p.members.includes(me)) ?? null;
  }

  private nameOf(id: number): string {
    if (id === BULL_ID) return "The Bull";
    return this.roster.find((m) => m.id === id)?.name ?? "?";
  }

  /** The top bar's party line: who is grouped, and who holds the door. */
  private updatePartyLine(): void {
    const p = this.myParty();
    const me = this.info?.playerId;
    if (!p) {
      this.partyEl.hidden = true;
      this.partyEl.textContent = "";
      const self = me === undefined ? [] : this.roster.filter((m) => m.id === me);
      // The rail's NO-PARTY face (keyracing.md A1): a lone name with a crown
      // over it reads "party of one, you lead it" — which is precisely the
      // lie the locked door then contradicts. Say "No party" and offer the
      // verb, so the door's answer is never a surprise.
      this.hud.renderParty(self.map((m) => ({ id: m.id, name: m.name, connected: true })), me, {
        meId: me ?? -1,
        createLabel: "Create party",
        onCreate: () => this.net.partyCreate(),
      });
      return;
    }
    const who = (id: number): string => (id === me ? "you" : this.nameOf(id));
    // A party of one says so, and says who holds the door (you): the old
    // "Party: you · Leader: you" read as a bug, and after A1 this shape is
    // the common case — it is what every solo now is.
    this.partyEl.textContent = p.members.length === 1
      ? "Party of one · you hold the door"
      : `Party: ${p.members.map(who).join(", ")} · Leader: ${who(p.host)}`;
    this.partyEl.hidden = false;
    const members = p.members.map((id) => ({ id, name: this.nameOf(id), connected: true }));
    this.hud.renderParty(members, p.host, {
      meId: me!,
      leaveLabel: "Leave party",
      onLeave: () => this.net.partyLeave(),
      onPromote: (id) => this.net.partyTransfer(id),
      openMenu: (x, y, title, items) => this.viewport.openMenu(x, y, title, items),
    });
  }

  /** The invite window (keyracing.md A3): one centred accept/reject surface
   *  for every pending invite addressed to me, replacing the top-bar strip.
   *  Rebuilt only when the invite set's signature changes — the invites are
   *  the server's word, and this is a projection of it, but the DOM must not
   *  churn under a click (traps/dom-rebuild.md). Closing the window WITHOUT
   *  deciding declines what it showed: the window is the invite's only
   *  surface, so dismissing it means "no" rather than leaving the inviter
   *  looking at a pending line forever. */
  private updateInviteWindow(): void {
    const me = this.info?.playerId;
    const mine = me === undefined ? [] : this.invites.filter((i) => i.to === me);
    const sig = mine.map((i) => i.from).sort((a, b) => a - b).join(",");
    if (sig === this.inviteSig) return;
    this.inviteSig = sig;
    this.closeInviteWindow();
    if (mine.length === 0) return;

    const modal = createGameModal(this.dom, "Party invitation", "party-invite");
    this.inviteModal = modal;
    const decided = new Set<number>();
    const decide = (from: number, accept: boolean): void => {
      if (decided.has(from)) return; // one answer per invite, whatever arrives after
      decided.add(from);
      if (accept) this.net.partyAccept(from);
      else this.net.partyDecline(from);
    };
    const body = modal.body;
    const copy = document.createElement("p");
    copy.className = "modal-copy";
    copy.textContent = mine.length === 1
      ? "Someone is offering you a place in their party."
      : "Several parties are waiting on an answer.";
    body.append(copy);

    const list = document.createElement("div");
    list.className = "invite-list";
    for (const inv of mine) {
      const row = document.createElement("div");
      row.className = "invite-row";
      const name = document.createElement("span");
      name.className = "invite-name";
      name.textContent = this.nameOf(inv.from);
      const accept = document.createElement("button");
      accept.type = "button";
      accept.className = "modal-primary";
      accept.textContent = "Accept";
      accept.addEventListener("click", () => decide(inv.from, true));
      const decline = document.createElement("button");
      decline.type = "button";
      decline.className = "modal-secondary";
      decline.textContent = "Decline";
      decline.addEventListener("click", () => decide(inv.from, false));
      row.append(name, accept, decline);
      list.append(row);
    }
    body.append(list);

    // Dismissed without a decision = declined (see the docstring). The modal
    // owns the × button, Escape and the backdrop, so all three are watched
    // here rather than re-implemented; `closeInviteWindow` is idempotent, so
    // the modal's own close and this one cannot fight.
    const dismiss = (): void => {
      for (const inv of mine) if (!decided.has(inv.from)) decide(inv.from, false);
    };
    modal.backdrop.querySelector(".game-modal-close")?.addEventListener("click", dismiss);
    modal.backdrop.addEventListener("keydown", (ev) => {
      if (ev.key === "Escape") dismiss();
    });
    modal.backdrop.addEventListener("pointerdown", (ev) => {
      if (ev.target === modal.backdrop) dismiss();
    });
  }

  /** Tear the invite window down (a decision landed, the set emptied, the
   *  scene is going away). Never declines anything — that is `dismiss`'s job,
   *  wired to the window's own close affordances. */
  private closeInviteWindow(): void {
    const modal = this.inviteModal;
    this.inviteModal = null;
    modal?.close();
  }

  // ---- the race (keyracing.md D2: one window, one row, one door face) -----
  //
  // Three surfaces and no more. The CONSENT WINDOW is the only modal, and only
  // for an open challenge; ARMED and RACING are carried by the party panel's row,
  // because "armed" is a registry state and a window that explains it teaches the
  // player the server's vocabulary (11-15). The DOOR keeps its own face: you click
  // the door you always click, and where the floor picker would have been there is
  // one question, because the terms were decided when the race armed.
  //
  // Everything here is a projection of `RaceState`. The client judges nothing: it
  // cannot arm a race, forfeit one, or talk about the other side's dungeon.

  /** The side of `race` I am on, or null. My HUB id is the handle — the wire's
   *  member ids are hub ids, and while I am in town I have one. A side whose door
   *  is open has no town presences (its members read `id: -1`), which is why the
   *  FLOOR matches the same packet by name instead (game.ts). */
  private mySide(race: RaceWire): 0 | 1 | null {
    const me = this.info?.playerId;
    if (me === undefined) return null;
    return raceMySide(race, (m) => m.id === me);
  }

  private isMine(race: RaceWire, side: 0 | 1): boolean {
    return this.mySide(race) === side;
  }

  private namesMyParty(race: RaceWire): boolean {
    return this.mySide(race) !== null;
  }

  /** A local countdown anchored to the moment the packet arrived (raceRow.ts).
   *  Kept as a method because the window's own clock reads it every second. */
  private remaining(ms: number): number {
    return raceRemaining(ms, this.raceSeenAt);
  }

  /** `m` in mm:ss. */
  private mmss(ms: number): string {
    return mmss(ms);
  }

  /** A side's display name. The host's name is the party's name (the crown is the
   *  same fact in the party list); once a side is in its floor its hub ids are -1,
   *  so fall back to the first name the roster carries. */
  private sideName(race: RaceWire, side: 0 | 1): string {
    const host = race.sides[side].host;
    const named = host >= 0 ? this.nameOf(host) : "";
    if (named && named !== "?") return named;
    return race.sides[side].members[0]?.name ?? "?";
  }

  /** Row + window + door, in one place: they read the same packet and must never
   *  disagree about whether a race is on. */
  private updateRaceSurfaces(): void {
    this.updateRaceRow();
    this.updateRaceWindow();
    this.refreshDoorPanel();
    this.updateRaceRowClock();
    this.updateRaceWindowClock();
    this.ensureRaceClockTimer();
  }

  /** The party panel's row — the whole of what an armed or running race looks
   *  like. A live commitment outranks an open offer (the offer keeps its window and
   *  its chat line); with nothing live, an open challenge shows its count and the
   *  verb that reopens the window, because dismissing a challenge is NOT answering
   *  it and the player must be able to come back. */
  private updateRaceRow(): void {
    this.hud.renderRaceRow(this.rowInfo(true));
  }

  /** The row's content, computed by the shared projection (raceRow.ts) so the
   *  town and a floor cannot describe the same race differently. `review` is the
   *  town's: the consent window lives here, not on a floor. */
  private rowInfo(review: boolean): RaceRowInfo | null {
    return raceRowInfo({
      races: this.races,
      seenAt: this.raceSeenAt,
      isMe: (m) => m.id === this.info?.playerId,
      sideName: (race, side) => this.sideName(race, side),
      hostIsMe: (host) => host === this.info?.playerId,
      onReview: review ? () => this.reopenRaceWindow() : undefined,
    });
  }

  /** Tick both clocks once a second while a race names my party. Text only — the
   *  row and the window are signature-guarded, so a ticking clock must not rebuild
   *  either (traps/dom-rebuild.md). */
  private raceClockTimerId: number | null = null;

  private ensureRaceClockTimer(): void {
    const live = this.races.some((r) => raceHasClock(r, (m) => m.id === this.info?.playerId));
    if (!live) {
      if (this.raceClockTimerId !== null) clearInterval(this.raceClockTimerId);
      this.raceClockTimerId = null;
      return;
    }
    if (this.raceClockTimerId !== null) return;
    this.raceClockTimerId = setInterval(() => {
      this.updateRaceRowClock();
      this.updateRaceWindowClock();
    }, 1000) as unknown as number;
  }

  private updateRaceRowClock(): void {
    const info = this.rowInfo(true);
    if (info) this.hud.setRaceClock(info.clock.text, info.clock.tone);
  }

  private raceWindowClock: HTMLElement | null = null;
  private raceWindowClockMs = 0;

  private updateRaceWindowClock(): void {
    const el = this.raceWindowClock;
    if (!el?.isConnected) return;
    const text = this.mmss(this.remaining(this.raceWindowClockMs));
    if (el.textContent !== text) el.textContent = text;
  }

  /** The open challenges naming me, newest last (issue order is the registry's). */
  private myOpenChallenges(): RaceWire[] {
    return this.races.filter((r) => r.state === "open" && this.mySide(r) !== null);
  }

  /** The armed-or-racing race my party is committed to, with my side. The door
   *  asks this before showing its picker: a committed party cannot open an ordinary
   *  floor (the server refuses it — a side that went off to play something else
   *  would leave a race waiting in a dungeon for nobody), so offering a picker the
   *  door will refuse is worse than showing the question the door does answer.
   *
   *  `raceWaitsOnMe` is the whole question: a side whose outcome landed is free
   *  again (the floor it walked out of forfeited it), even though the race is still
   *  armed while the other side's window runs. The row asks the same thing, so the
   *  door and the row can never disagree about whether I am still in it. */
  private myCommittedRace(): { race: RaceWire; side: 0 | 1 } | null {
    for (const race of this.races) {
      const side = raceWaitsOnMe(race, (m) => m.id === this.info?.playerId);
      if (side !== null) return { race, side };
    }
    return null;
  }

  /** Re-open the consent window on demand (the row's Review verb): the signature
   *  is dropped so a window that is already up is rebuilt rather than skipped. */
  private reopenRaceWindow(): void {
    this.raceSig = "";
    this.updateRaceWindow();
  }

  /**
   * The consent window: one per open challenge naming my party, stepped when
   * several do (an account may be named by any number of open offers — only ARMING
   * claims a slot). Every member of BOTH sides sees it, which is what unanimity
   * looks like from a chair; the ticks say who is holding it up, because with no
   * countdown the interface has to name the reason it has not armed.
   *
   * Dismissing it is NOT an answer. Silence is not consent and expiry is the
   * issuer's out, so × leaves the vote uncast and the row keeps the challenge
   * visible with the verb that brings the window back.
   */
  private updateRaceWindow(): void {
    const open = this.myOpenChallenges();
    if (open.length === 0) {
      this.closeRaceWindow();
      return;
    }
    if (this.raceShown >= open.length) this.raceShown = 0;
    const race = open[this.raceShown]!;
    const side = this.mySide(race);
    const me = this.info?.playerId;
    // mySide() answered non-null a moment ago, which is only possible with a
    // session — but the window is built from a packet that can land before one.
    if (side === null || me === undefined) return;
    const sig = [
      race.id,
      race.state,
      race.category,
      race.sides.map((s) => `${s.host}:${s.members.map((m) => `${m.id}${m.accepted ? "y" : "n"}${m.elo ?? ""}${m.win ?? ""}`).join(",")}`).join("|"),
      open.length,
    ].join(";");
    if (sig === this.raceSig && this.raceModal?.backdrop.isConnected) return;
    this.raceSig = sig;
    this.closeRaceWindow();

    const modal = createGameModal(this.dom, `Challenge: ${this.sideName(race, (1 - side) as 0 | 1)}`, "race-challenge");
    this.raceModal = modal;
    modal.backdrop.classList.add("race-backdrop");
    const body = modal.body;

    const head = document.createElement("div");
    head.className = "race-head";
    head.append(document.createTextNode(
      open.length > 1 ? `${this.raceShown + 1} of ${open.length} · ` : "",
    ));
    const clock = document.createElement("span");
    clock.className = "race-clock";
    clock.textContent = this.mmss(this.remaining(race.expiresInMs));
    this.raceWindowClock = clock;
    this.raceWindowClockMs = race.expiresInMs;
    head.append(clock);
    body.append(head);

    body.append(this.raceTerms(race));

    const cols = document.createElement("div");
    cols.className = "race-cols";
    cols.append(
      this.raceSidePane(race, side, "Your party", true),
      this.raceSidePane(race, (1 - side) as 0 | 1, "Their party", false),
    );
    body.append(cols);

    body.append(this.raceActions(race, side, me));
  }

  /** The locked terms. One strip of state, not a paragraph: the window needs no
   *  prose the ticks and the counts do not already carry. */
  private raceTerms(race: RaceWire): HTMLElement {
    const box = document.createElement("div");
    box.className = "race-terms";
    const floor = FLOORS.find((f) => f.id === race.floor)?.name ?? `floor ${race.floor}`;
    const term = (k: string, v: string, cls = ""): void => {
      const t = document.createElement("span");
      t.className = "race-term";
      const kk = document.createElement("span");
      kk.className = "race-term-k";
      kk.textContent = k;
      const vv = document.createElement("span");
      vv.className = "race-term-v" + (cls ? " " + cls : "");
      vv.textContent = v;
      t.append(kk, vv);
      box.append(t);
    };
    term("Floor", floor);
    term("Size", race.size);
    term("Stakes", race.rated ? "rated" : "unrated", race.rated ? "rated" : "");
    term("Ladder", `${race.category}s`);
    return box;
  }

  /** One side: its roster, each player's rating and what THEY stand to win or
   *  lose, and the accept tick. The numbers are the server's `previewRace` rows —
   *  the same math the verdict runs — so the window cannot promise a delta the
   *  resolution fails to deliver. An unrated race has no numbers at all: a column
   *  of dashes reads as a bug, not as "nothing at stake". */
  private raceSidePane(race: RaceWire, side: 0 | 1, title: string, mine: boolean): HTMLElement {
    const sec = document.createElement("section");
    sec.className = "race-side" + (mine ? " mine" : " them") + (race.rated ? "" : " plain");
    const head = document.createElement("div");
    head.className = "race-side-head";
    const who = document.createElement("span");
    who.className = "race-side-who";
    who.textContent = title;
    head.append(who);
    const agg = race.sides[side].agg;
    if (agg !== null) {
      const a = document.createElement("span");
      a.className = "race-side-agg";
      a.textContent = `agg ${agg}`;
      head.append(a);
    }
    sec.append(head);

    const yes = race.sides[side].members.filter((m) => m.accepted).length;
    for (const m of race.sides[side].members) {
      const row = document.createElement("div");
      row.className = "race-row" + (m.id === this.info?.playerId ? " me" : "") + (m.races === 0 ? " prov" : "");
      const crown = document.createElement("span");
      crown.className = "race-crown" + (m.id === race.sides[side].host ? "" : " off");
      crown.textContent = "♛";
      crown.title = "Party host";
      const name = document.createElement("span");
      name.className = "race-name";
      name.textContent = m.id === this.info?.playerId ? `${m.name} (you)` : m.name;
      row.append(crown, name);
      const elo = document.createElement("span");
      elo.className = "race-elo";
      elo.textContent = m.elo === undefined ? "" : String(m.elo);
      const rd = document.createElement("span");
      rd.className = "race-rd";
      rd.textContent = m.elo === undefined ? "" : m.races === 0 ? "new" : String(m.rd ?? "");
      rd.title = "Rating deviation — the reason a swing is big";
      const stake = document.createElement("span");
      stake.className = "race-stake";
      if (m.win !== undefined && m.loss !== undefined) {
        const w = document.createElement("span");
        w.className = "race-win";
        w.textContent = `+${m.win}`;
        const l = document.createElement("span");
        l.className = "race-loss";
        l.textContent = `−${Math.abs(m.loss)}`;
        stake.append(w, l);
      }
      const tick = document.createElement("span");
      tick.className = "race-tick" + (m.accepted ? " yes" : "");
      tick.textContent = m.accepted ? "✓" : "○";
      tick.title = m.accepted ? "Accepted" : "Waiting for their answer";
      row.append(elo, rd, stake, tick);
      sec.append(row);
    }

    const foot = document.createElement("div");
    foot.className = "race-side-foot" + (yes === race.sides[side].members.length ? " full" : "");
    foot.textContent = `${yes} of ${race.sides[side].members.length} accepted`;
    sec.append(foot);
    return sec;
  }

  /** The verbs. Accept is for everyone except the issuing host, whose issue IS
   *  their acceptance. Reject belongs to a HOST — either side's, which is why the
   *  issuer's copy says Cancel: the design forbids withdrawing an ARMED race, and
   *  this is not one, it is an offer nobody has committed to. A MEMBER has no
   *  reject verb on the wire and never will have: their opt-out is leaving the
   *  party, which is what the server does with this click — and the button says
   *  Reject anyway, because that is what it looks like from their chair, so the
   *  click asks first (one un-hedged click would otherwise dissolve a five-person
   *  party). */
  private raceActions(race: RaceWire, side: 0 | 1, me: number): HTMLElement {
    const actions = document.createElement("div");
    actions.className = "race-actions";
    const iAmHost = race.sides[0].host === me || race.sides[1].host === me;
    const iIssued = race.from === me;

    const accept = document.createElement("button");
    accept.type = "button";
    accept.className = "modal-primary";
    accept.textContent = "Accept";
    accept.addEventListener("click", () => {
      accept.disabled = true; // one answer per race; the answer is the rebroadcast tick
      this.net.raceAccept(race.id);
    });
    if (!iIssued && !race.sides[side].members.find((m) => m.id === me)?.accepted) actions.append(accept);

    const reject = document.createElement("button");
    reject.type = "button";
    reject.className = "modal-secondary";
    reject.textContent = iIssued ? "Cancel challenge" : "Reject";
    actions.append(reject);

    if (iAmHost) {
      reject.addEventListener("click", () => {
        reject.disabled = true;
        this.net.raceReject(race.id);
      });
    } else {
      // A member's Reject is `PartyLeave` wearing the player's word. Ask first:
      // the party is the thing being spent, and it is not recoverable from here.
      const showAsk = (): void => {
        actions.textContent = "";
        const warn = document.createElement("span");
        warn.className = "race-confirm";
        warn.textContent = "Rejecting leaves your party.";
        const leave = document.createElement("button");
        leave.type = "button";
        leave.className = "modal-primary";
        leave.textContent = "Leave party";
        leave.addEventListener("click", () => {
          leave.disabled = true;
          this.net.partyLeave();
        });
        const back = document.createElement("button");
        back.type = "button";
        back.className = "modal-secondary";
        back.textContent = "Back";
        back.addEventListener("click", () => {
          this.raceSig = "";
          this.updateRaceWindow();
        });
        actions.append(warn, leave, back);
      };
      reject.addEventListener("click", showAsk);
    }
    return actions;
  }

  private closeRaceWindow(): void {
    const modal = this.raceModal;
    this.raceModal = null;
    this.raceWindowClock = null;
    if (this.raceSig === "" && !modal) return;
    modal?.close();
  }

  /** Stop the clock entirely (scene teardown). */
  private stopRaceClock(): void {
    if (this.raceClockTimerId !== null) clearInterval(this.raceClockTimerId);
    this.raceClockTimerId = null;
  }

  // ---- issuing a challenge (the terms picker a host issues from) ----------

  /**
   * The issuer's picker: floor type, size (medium|large — a small floor is a
   * 40-second floor and a 40-second race measures who clicked first), and rated.
   * No seed and no ladder: the server draws the seed at arm and the ladder is
   * `max(side sizes)`, which is a fact about both parties, not a choice.
   */
  openTermsPanel(target: number): void {
    this.stopWalk();
    this.viewport.closeMenu();
    this.closeTermsPanel();
    const modal = createGameModal(this.dom, `Challenge ${this.nameOf(target)}'s party`, "race-terms");
    this.termsModal = modal;
    const body = modal.body;
    const hint = document.createElement("div");
    hint.className = "modal-hint";
    hint.textContent = "Both parties run the same dungeon. The seed is drawn when the last player accepts, and nobody is shown it.";
    body.append(hint);

    const floorSel = document.createElement("select");
    for (const f of FLOORS) {
      const opt = document.createElement("option");
      opt.value = String(f.id);
      opt.textContent = f.name;
      floorSel.appendChild(opt);
    }
    const sizeSel = document.createElement("select");
    for (const s of ["medium", "large"] as const) {
      const opt = document.createElement("option");
      opt.value = s;
      opt.textContent = s[0]!.toUpperCase() + s.slice(1);
      sizeSel.appendChild(opt);
    }
    const ratedSel = document.createElement("select");
    for (const [v, label] of [["1", "rated (moves the ladder)"], ["0", "unrated (changes nothing)"]] as const) {
      const opt = document.createElement("option");
      opt.value = v;
      opt.textContent = label;
      ratedSel.appendChild(opt);
    }
    const field = (text: string, sel: HTMLSelectElement): HTMLLabelElement => {
      const label = document.createElement("label");
      label.className = "modal-field";
      const span = document.createElement("span");
      span.textContent = text;
      label.append(span, sel);
      return label;
    };

    const send = document.createElement("button");
    send.type = "button";
    send.className = "modal-primary";
    send.textContent = "Challenge";
    if (FLOORS.length === 0) send.disabled = true;
    send.addEventListener("click", () => {
      send.disabled = true;
      this.net.raceChallenge(target, Number(floorSel.value), sizeSel.value as "medium" | "large", ratedSel.value === "1");
      this.chat.system("Challenge sent.");
      this.closeTermsPanel();
    });
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "modal-secondary";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => this.closeTermsPanel());
    const row = document.createElement("div");
    row.className = "modal-actions";
    row.append(send, cancel);
    body.append(
      ...(FLOORS.length === 0 ? [Object.assign(document.createElement("div"), { className: "modal-hint", textContent: "no floor types yet (knobs app → Floors)" })] : []),
      field("Floor type", floorSel),
      field("Size", sizeSel),
      field("Stakes", ratedSel),
      row,
    );
  }

  private closeTermsPanel(): void {
    const modal = this.termsModal;
    this.termsModal = null;
    modal?.close();
  }

  /** Hub-only screen-space social chrome: the party host marker and transient
   *  speech bubbles. Both follow INTERPOLATED positions, so they glide with
   *  the same visual motion as the heroes beneath them. */
  private overlay(o: OverlayApi): void {
    if (!this.heroAssets) return;
    this.drawHostMarker(o);
    this.drawBullLabel(o);
    this.drawChatBubbles(o);
  }

  /** The renderer's generic enemy path intentionally has no name tags; the
   *  hub adds the service NPC's readable identity in the same screen-space
   *  overlay as social chrome. */
  private drawBullLabel(o: OverlayApi): void {
    const bull = this.npcs.find((n) => n.id === "bull");
    const pos = o.positions.get(enemyKey(BULL_ID));
    if (!bull || !pos) return;
    const t = tileToScreen(pos.x, pos.y, o.isoUnit, o.camX, o.camY);
    const ctx = o.ctx;
    const y = t.y + HALF_H * o.isoUnit - 92 * o.isoUnit;
    ctx.save();
    ctx.font = `600 ${Math.max(10, 12 * o.isoUnit)}px ui-monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.lineWidth = Math.max(2, 3 * o.isoUnit);
    ctx.strokeStyle = "rgba(8, 10, 13, 0.92)";
    ctx.strokeText("The Bull", t.x, y);
    ctx.fillStyle = "#f1eee6";
    ctx.fillText("The Bull", t.x, y);
    ctx.restore();
  }

  /** The party marker (hub.md): a small gold diamond over the host's name
   *  tag — "a small marker, not a crown". */
  private drawHostMarker(o: OverlayApi): void {
    const p = this.myParty();
    if (!p || !this.heroAssets) return;
    const pos = o.positions.get(playerKey(p.host));
    if (!pos) return;
    const [, ah] = this.heroAssets.hero.anchor;
    const t = tileToScreen(pos.x, pos.y, o.isoUnit, o.camX, o.camY);
    // The renderer's feet anchor (tileToScreen.y + HALF_H·u) minus the
    // sprite height, then up into the gap between the head and the name tag
    // (the tag's text bottom sits 9·u above the head).
    const cx = t.x;
    const cy = t.y + HALF_H * o.isoUnit - ah * o.isoUnit - 14 * o.isoUnit;
    const s = 3 * o.isoUnit;
    o.ctx.save();
    o.ctx.translate(cx, cy);
    o.ctx.rotate(Math.PI / 4);
    o.ctx.fillStyle = "#c8a24a"; // the door's gold — the host's one warm thing
    o.ctx.strokeStyle = "rgba(0,0,0,0.85)";
    o.ctx.lineWidth = Math.max(1, 1.5 * o.isoUnit);
    o.ctx.fillRect(-s, -s, s * 2, s * 2);
    o.ctx.strokeRect(-s, -s, s * 2, s * 2);
    o.ctx.restore();
  }

  /** Short-lived in-world chat (chatBubbles.ts, shared with the floor). A new
   *  line from one speaker replaces their previous bubble; the shared
   *  ChatPanel keeps the full transcript. The town is one screen with no fog,
   *  so a bubble is clamped into the canvas rather than skipped. */
  private drawChatBubbles(o: OverlayApi): void {
    if (!this.heroAssets) return;
    drawChatBubbles(
      { ctx: o.ctx, positions: o.positions, camX: o.camX, camY: o.camY, isoUnit: o.isoUnit, cssW: o.cssW, cssH: o.cssH, now: o.now },
      this.chatBubbles,
      this.heroAssets.hero.anchor[1],
      false,
    );
  }

  /** Keep existing slot assignments, give new members the lowest free index,
   *  and free the indices of members who left (so a busy town of 30 slots
   *  never runs out through churn). */
  private assignSlots(players: readonly TownPresence[]): void {
    const ids = new Set(players.map((p) => p.id));
    for (const [id, idx] of [...this.slotOf]) {
      if (!ids.has(id)) {
        this.slotOf.delete(id);
        this.slotTaken.delete(idx);
      }
    }
    for (const m of players) {
      if (this.slotOf.has(m.id)) continue;
      let i = 0;
      while (this.slotTaken.has(i)) i++;
      this.slotOf.set(m.id, i);
      this.slotTaken.add(i);
    }
  }

  /** A member's town slot (roster members always have one; the fallback
   *  covers the frame between a Welcome and the roster that carries it). */
  private slotFor(id: number): Vec2 {
    const i = this.slotOf.get(id);
    return SLOTS[i === undefined ? 0 : i % SLOTS.length]!;
  }

  /** Where a member renders.
   *
   *  MINE is the tile I walked to — never the echo of my own report: a round
   *  trip must not rubber-band my hero, which is why a floor client never
   *  accepts its own position back either.
   *
   *  ANYONE ELSE is at the tile they reported (the hub relayed it). A member
   *  whose first report has not landed yet, or a wire value that is somehow
   *  not a whole number, falls back to their spawn slot: a NaN position draws
   *  nothing at all, which reads as a player who vanished rather than a
   *  coordinate that was never valid.
   */
  private posFor(m: TownPresence): Vec2 {
    if (this.info && m.id === this.info.playerId) return this.mePos ?? this.slotFor(m.id);
    if (!m.pos) return this.slotFor(m.id);
    // Clamped again on the way out: the server clamps what it relays, but a
    // tile beyond the plane must not render a hero off the world either.
    return {
      x: Math.max(0, Math.min(PLANE_W - 1, m.pos.x)),
      y: Math.max(0, Math.min(PLANE_H - 1, m.pos.y)),
    };
  }

  private pushObs(): void {
    const obs = this.synthesize();
    this.debug.obs = obs;
    this.debug.me = this.mePos;
    this.debug.walkTarget = this.walkTo;
    this.viewport.ingest(obs, this.tick++);
  }

  /** The town as an Observation: one revealed flat plane + the roster at
   *  their cosmetic tiles. No fog (the whole town is revealed), no floor
   *  enemies/items/boss — the one synthetic NPC is the hub-only Bull render
   *  projection and never reaches a run. The door is NOT an obs.door entry (that would draw the
   *  floor's keyed-door art): it is a world prop, and the hub's own click
   *  rules below own its footprint. */
  private synthesize(): Observation {
    return {
      width: PLANE_W,
      height: PLANE_H,
      enemies: this.npcs.map((npc) => this.npcAt(npc)),
      map: this.planeMap,
      revealed: this.planeRevealed,
      terrain: this.planeTerrain,
      base: { x: PLANE_W / 2, y: PLANE_H / 2 },
      // The town has no return portal (the dungeon door is a world prop,
      // not an obs entity) and no exit vote (no sim in the hub).
      returnPortal: null,
      exit: { remaining: null, accepted: [] },
      boss: null,
      keys: [],
      doors: [],
      stones: [],
      drops: [], // the town has no loot (no sim)
      sack: null, // and no base room
      players: this.roster.map((m) => this.playerAt(m)),
      complete: false,
      // The committed table, whole (ObservedFloor.combat's shape) — a render
      // projection only: the interpolation's step clock, and walking is the
      // only motion here, and it walks at exactly this cadence, so the steps
      // join into one gait. The town has no run, so there is no live table.
      combat: COMBAT,
    };
  }

  private npcAt(npc: HubNpcWire): ObservedEnemy {
    return {
      id: BULL_ID,
      kind: npc.id,
      tint: "canon",
      x: npc.x,
      y: npc.y,
      hp: 1,
      maxHp: 1,
      alive: true,
      attack: null,
      moveSpeedTicks: BULL_MOVE_TICKS,
    };
  }

  private playerAt(m: TownPresence): ObservedPlayer {
    const pos = this.posFor(m);
    return {
      id: m.id,
      name: m.name,
      x: pos.x,
      y: pos.y,
      // No sim → no HP; these feed only an overlay the hub doesn't draw.
      hp: 1,
      maxHp: 1,
      alive: true,
      attack: null,
      attackTarget: null,
      keys: [],
      carried: [],
      items: [],
      equipment: Object.fromEntries(EQUIP_SLOTS.map((s) => [s, null])) as Record<EquipSlot, number | null>,
      pgs: "none",
      classKind: "knight",
      level: 1,
      xp: 0,
      abilityReadyAt: [0, 0, 0],
      pendingHit: null,
      damageImmuneUntil: 0,
      reflectUntil: 0,
      reflectPct: 0,
    };
  }

  // ---- walking (cosmetic and self-reported: the hub relays, it never moves) ----

  private walkStep = (): void => {
    const next = this.walkPath.shift();
    if (!this.walkTo || !next) {
      this.stopWalk();
      return;
    }
    this.mePos = next;
    this.pushObs();
    this.sendPos(); // the town sees every step, not just the arrival
    if (this.walkPath.length === 0) this.stopWalk();
  };

  private startWalk(to: Vec2): void {
    if (!this.mePos || !this.isWalkable(to.x, to.y)) return;
    const path = this.findWalkPath(this.mePos, to);
    if (path.length === 0 && (this.mePos.x !== to.x || this.mePos.y !== to.y)) {
      this.chat.invalid("The plaza terrain blocks the way.");
      return;
    }
    this.walkTo = to;
    this.walkPath = path;
    if (this.walkTimer === null) this.walkTimer = window.setInterval(this.walkStep, WALK_MS);
  }

  /** Small deterministic BFS for the cosmetic town only. The floor keeps
   *  using the server's authoritative routing; this merely keeps local hub
   *  walking out of ponds and furniture. */
  private findWalkPath(from: Vec2, to: Vec2): Vec2[] {
    if (from.x === to.x && from.y === to.y) return [];
    const count = PLANE_W * PLANE_H;
    const prev = new Int32Array(count).fill(-1);
    const queue = new Int32Array(count);
    const start = from.y * PLANE_W + from.x;
    const goal = to.y * PLANE_W + to.x;
    prev[start] = start;
    queue[0] = start;
    let head = 0;
    let tail = 1;
    const sx = Math.sign(to.x - from.x) || 1;
    const sy = Math.sign(to.y - from.y) || 1;
    const dirs: ReadonlyArray<readonly [number, number]> = [
      [sx, sy], [sx, 0], [0, sy], [sx, -sy], [-sx, sy], [-sx, 0], [0, -sy], [-sx, -sy],
    ];
    while (head < tail && prev[goal] === -1) {
      const at = queue[head++]!;
      const x = at % PLANE_W;
      const y = Math.floor(at / PLANE_W);
      for (const [dx, dy] of dirs) {
        const nx = x + dx;
        const ny = y + dy;
        if (!this.isWalkable(nx, ny)) continue;
        // Do not shave diagonally between two solid corners.
        if (dx !== 0 && dy !== 0 && (!this.isWalkable(x + dx, y) || !this.isWalkable(x, y + dy))) continue;
        const ni = ny * PLANE_W + nx;
        if (prev[ni] !== -1) continue;
        prev[ni] = at;
        queue[tail++] = ni;
      }
    }
    if (prev[goal] === -1) return [];
    const path: Vec2[] = [];
    for (let at = goal; at !== start; at = prev[at]!) {
      path.push({ x: at % PLANE_W, y: Math.floor(at / PLANE_W) });
    }
    path.reverse();
    return path;
  }

  /** Announce where my hero is (ClientOp.HubMove): on spawn and on every
   *  walked step. Never for anyone else, never from a roster echo — my own
   *  loop would be an echo chamber. The hub clamps the tile into the plane
   *  and rebroadcasts the roster (coalesced), and that is the whole journey:
   *  no sim, no order, no authority. */
  private sendPos(): void {
    if (!this.mePos) return;
    this.net.hubMove(this.mePos.x, this.mePos.y);
  }

  private stopWalk(): void {
    if (this.walkTimer !== null) {
      clearInterval(this.walkTimer);
      this.walkTimer = null;
    }
    this.walkTo = null;
    this.walkPath = [];
    this.debug.walkTarget = null;
  }

  // ---- the interaction model ---------------------------------------------

  private inPlane(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < PLANE_W && y < PLANE_H;
  }

  private isWalkable(x: number, y: number): boolean {
    return this.inPlane(x, y) && !this.isDoor(x, y) && this.planeMap[y * PLANE_W + x] === 1;
  }

  /** The door's footprint: its row, and the whole wedge north of it within
   *  its width. The art is taller than that wedge, so every pixel of the
   *  door face belongs to the door — clicking it is clicking the door, and
   *  walking into it is not walking. */
  private isDoor(x: number, y: number): boolean {
    return this.inPlane(x, y) && x + y <= DOOR_ROW && Math.abs(x - y) <= DOOR_W - 1;
  }

  private playerAtTile(x: number, y: number): TownPresence | null {
    for (const m of this.roster) {
      const s = this.posFor(m);
      if (s.x === x && s.y === y) return m;
    }
    return null;
  }

  private bullAtTile(x: number, y: number): ObservedEnemy | null {
    const bull = this.npcs.find((n) => n.id === "bull");
    return bull && bull.x === x && bull.y === y ? this.npcAt(bull) : null;
  }

  /** Left-click intent: the door, a player, or a walk (a standing order). */
  private primaryAction(x: number, y: number): PrimaryAction {
    if (this.isDoor(x, y)) {
      return {
        kind: "interact",
        label: "The dungeon door",
        obj: { kind: "boss", id: -1, x: DOOR_ANCHOR.x, y: DOOR_ANCHOR.y, state: "portal" },
      };
    }
    const bull = this.bullAtTile(x, y);
    if (bull) {
      return {
        kind: "interact",
        label: "Talk to The Bull",
        obj: { kind: "boss", id: BULL_ID, x: bull.x, y: bull.y, state: "portal" },
      };
    }
    if (!this.isWalkable(x, y)) return { kind: "none" };
    if (this.playerAtTile(x, y)) return { kind: "none" };
    return { kind: "walk", label: "Walk here", x, y };
  }

  private onPrimaryAction(a: PrimaryAction): void {
    if (a.kind === "walk") {
      this.startWalk({ x: a.x, y: a.y });
      return;
    }
    if (a.kind === "interact" && a.obj.kind === "boss" && a.obj.id === BULL_ID) {
      this.stopWalk();
      this.openBullPanel();
      return;
    }
    if (a.kind === "interact" && a.obj.kind === "boss" && a.obj.id === -1) {
      // The door (hub.md, amended by keyracing.md A1): the door answers to a
      // PARTY. No party is refused with the reason and the way out (the
      // confirm opens in its "create a party" face); a party's door is the
      // HOST's control — a non-host gets the host's name, not the confirm
      // (the server refuses the packet too; the client's gating is courtesy,
      // never authority).
      const p = this.myParty();
      if (p && p.host !== this.info?.playerId) {
        this.stopWalk(); // a note is still "not a walk" — stop, like any other
        this.chat.invalid(`${this.nameOf(p.host)} holds the door for this party.`);
        return;
      }
      // Confirm overlay: floor type + size, then the OpenDoor op.
      this.openDoorPanel();
      return;
    }
    // Anything that isn't a walk stops the walk (the same "new order
    // replaces the old" rule the floor's standing orders follow).
    this.stopWalk();
  }

  /** Right-click menu: the door (party + host gated), and the heroes —
   *  "Create party" / "Invite to party" / "Leave party" (the party verbs,
   *  keyracing.md A1). */
  private contextItems(x: number, y: number): { title: string; items: CtxItem[] } | null {
    if (this.isDoor(x, y)) {
      const p = this.myParty();
      const me = this.info?.playerId;
      const items: CtxItem[] = [];
      if (!p) {
        // The retired solo door, made explicit: you cannot knock as nobody.
        items.push({ label: "needs a party", disabled: true });
        items.push({ label: "Create party", kind: "interact", run: () => this.net.partyCreate() });
      } else if (p.host === me) {
        items.push({ label: "Enter the dungeon", kind: "interact", run: () => this.openDoorPanel() });
      } else {
        // A non-host gets the host's name, disabled — the server refuses a
        // non-host's OpenDoor packet regardless (it never trusts this).
        items.push({ label: `host: ${this.nameOf(p.host)}`, disabled: true });
      }
      items.push({
        label: "Examine",
        kind: "examine",
        run: () => this.chat.system("Beyond this door, a new dungeon waits. A party — even a party of one — opens it."),
      });
      return { title: "The dungeon door", items };
    }
    const bull = this.bullAtTile(x, y);
    if (bull) return this.contextItemsForBull(x, y);
    const m = this.playerAtTile(x, y);
    if (m) return this.contextItemsForPlayer(m, x, y);
    if (this.isWalkable(x, y)) {
      return {
        title: "The plaza",
        items: [{ label: "Walk here", kind: "walk", run: () => this.walkHere(x, y) }],
      };
    }
    if (this.inPlane(x, y)) {
      const terrain = this.planeTerrain[y * PLANE_W + x];
      const pond = terrain === TERRAIN_POND;
      return {
        title: pond ? "Reflecting pool" : "Stone seat",
        items: [{
          label: "Examine",
          kind: "examine",
          run: () => this.chat.system(pond ? "Cold water breaks the civic square into quieter courts." : "A low stone seat beside the water."),
        }],
      };
    }
    return null;
  }

  private contextItemsForBull(walkX: number, walkY: number): { title: string; items: CtxItem[] } {
    return {
      title: "The Bull",
      items: [
        { label: "Talk-to", kind: "interact", run: () => this.openBullPanel() },
        { label: "Examine", kind: "examine", run: () => this.chat.system("The Bull stands watch by the dungeon door, sizing up every adventurer who passes.") },
        { label: "Walk here", kind: "walk", run: () => this.walkHere(walkX, walkY) },
      ],
    };
  }

  /** Build a social menu for one roster identity. This is deliberately
   * identity-based rather than tile-based: a torso can overlap another tile,
   * and several interpolated heroes can share one authoritative tile. */
  private contextItemsForPlayer(m: TownPresence, walkX: number, walkY: number): CtxMenu {
    const me = this.info?.playerId;
    const items: CtxItem[] = [];
    if (m.id === me) {
      // Your own hero: the party's front door and its exit. With no party
      // there is nothing to leave and everything to found (A1 — the door
      // needs a party, so this is the first verb a fresh arrival needs);
      // with one, leaving is the only opt-out.
      if (this.myParty()) {
        items.push({ label: "Leave party", kind: "interact", run: () => this.net.partyLeave() });
      } else {
        items.push({ label: "Create party", kind: "interact", run: () => this.net.partyCreate() });
      }
      items.push({ label: "Examine", kind: "examine", run: () => this.examinePlayer(m.id) });
    } else {
      // The others: a seat in YOUR party — which requires having one (A1:
      // an invite offers a place in something that exists, it no longer
      // founds one). The server is the judge on everything else (accounts,
      // cap, their own party); a refusal comes back as an Error note. A
      // party member + I hold the lead → the lead is transferable
      // (PartyTransfer — the founder hosts, the host can hand the door on).
      const p = this.myParty();
      if (p && p.host === me && p.members.includes(m.id)) {
        items.push({ label: "Make leader", kind: "interact", run: () => this.net.partyTransfer(m.id) });
      }
      if (!p || !p.members.includes(m.id)) {
        if (p) items.push({ label: "Invite to party", kind: "interact", run: () => this.net.partyInvite(m.id) });
        else items.push({ label: "create a party first", disabled: true });
      }
      // Challenge to a race (keyracing.md C1/D2): issued AT a party — any of its
      // members names the side, and the server resolves that side's host. My party's
      // host issues it (the server is the judge, as everywhere here); a player with no
      // party has nothing to race with, so the verb does not appear.
      const theirs = this.parties.find((pp) => pp.members.includes(m.id));
      if (p && p.host === me && theirs && theirs !== p) {
        items.push({ label: "Challenge to a race", kind: "interact", run: () => this.openTermsPanel(m.id) });
      }
      items.push({ label: "Examine", kind: "examine", run: () => this.examinePlayer(m.id) });
    }
    items.push({ label: "Walk here", kind: "walk", run: () => this.walkHere(walkX, walkY) });
    // The rating plate (keyracing.md D1b): a readout under the name, above the
    // verbs. It is what a town player is on paper, and it is the one piece of the
    // menu a player reads rather than clicks — which is why it gets its own block
    // instead of being squeezed into a verb row.
    return { title: m.name, items, plate: this.plateFor(m.id) };
  }

  /** The menu's "Walk here" (the canvas click path never reaches it — the
   *  menu is DOM, so the walk has to be driven from here). */
  private walkHere(x: number, y: number): void {
    this.startWalk({ x, y });
    this.viewport.pushMenuMarker(null, { x, y });
  }

  // ---- The Bull (records and gear) ----------------------------------

  private openBullPanel(): void {
    this.stopWalk();
    this.viewport.closeMenu();
    this.closeDoorPanel();
    if (this.bullModal?.backdrop.isConnected) return;
    this.closeBullPanel(); // discard a modal closed through its own chrome
    this.bullModal = createGameModal(this.dom, "The Bull", "bull-dialog");
    this.renderBullDialogue();
  }

  private closeBullPanel(): void {
    const modal = this.bullModal;
    this.bullModal = null;
    modal?.close();
  }

  private renderBullDialogue(): void {
    const modal = this.bullModal;
    if (!modal) return;
    const body = modal.body;
    body.replaceChildren();

    const dialogue = document.createElement("p");
    dialogue.className = "modal-copy bull-dialogue";
    // This is intentionally a short, replaceable playtest script: the
    // content owner can edit these two sentences without touching the
    // service protocol or sim.
    dialogue.textContent = "Every attempt tells a story. Visit The boards to study your route, compare times, or share a floor that didn't go your way.";
    body.append(dialogue);

    const actions = document.createElement("div");
    actions.className = "modal-actions";
    const edit = document.createElement("button");
    edit.type = "button";
    edit.className = "modal-primary";
    edit.textContent = "Show me my gear";
    edit.addEventListener("click", () => this.renderBullEditor());
    actions.append(edit);
    const records = document.createElement("a");
    records.href = "/leaderboard";
    records.dataset.records = "";
    records.className = "linkish";
    records.textContent = "The boards →";
    const mine = document.createElement("a");
    mine.href = "/leaderboard?view=mine";
    mine.dataset.records = "";
    mine.className = "linkish";
    mine.textContent = "My floors →";
    actions.append(records, mine);
    const close = document.createElement("button");
    close.type = "button";
    close.className = "modal-secondary";
    close.textContent = "Close";
    close.addEventListener("click", () => this.closeBullPanel());
    actions.append(close);
    body.append(actions);
  }

  private renderBullEditor(): void {
    const current = this.playtestLoadout;
    const modal = this.bullModal;
    if (!modal) return;
    if (!current) {
      this.chat.invalid("Your saved gear is unavailable. Sign in again to edit it.");
      return;
    }
    const catalog = this.playtestCatalog ?? { items: ITEMS, classes: CLASS_CATALOG, combat: COMBAT };
    const editor = document.createElement("div");
    editor.className = "modal-form bull-editor";
    const fields = document.createElement("div");
    fields.className = "loadout-grid loadout-character";
    editor.append(fields);

    const classSel = document.createElement("select");
    classSel.id = "loadout-class";
    for (const cls of CLASSES) {
      const option = document.createElement("option");
      option.value = cls;
      option.textContent = catalog.classes.defs[cls].name;
      option.selected = cls === current.classKind;
      classSel.append(option);
    }
    fields.append(this.bullField("Class", classSel));

    // Levels are out of the playtest: the server pins the level
    // (shared PLAYTEST_HERO_LEVEL) and the editor offers no picker — the
    // preview and the save use the level the server gave this account.
    const level = Math.max(CLASS_MIN_LEVEL, Math.min(CLASS_MAX_LEVEL, current.level));

    const build = document.createElement("div");
    build.className = "loadout-build";
    const gear = document.createElement("div");
    gear.className = "loadout-gear";
    const summary = document.createElement("aside");
    summary.className = "loadout-summary";
    const summaryTitle = document.createElement("strong");
    summaryTitle.textContent = "Build totals";
    const totals = document.createElement("div");
    summary.append(summaryTitle, totals);
    build.append(gear, summary);
    editor.append(build);

    const equipment: Partial<Record<EquipSlot, HTMLSelectElement>> = {};
    const previews = new Map<EquipSlot, HTMLDivElement>();
    for (const slot of EQUIP_SLOTS) {
      const card = document.createElement("div");
      card.className = "loadout-item";
      card.dataset.slot = slot;
      const sel = document.createElement("select");
      sel.dataset.slot = slot;
      if (slot !== "weapon") {
        const empty = document.createElement("option");
        empty.value = "";
        empty.textContent = "None";
        sel.append(empty);
      }
      for (const item of catalog.items.filter((candidate) => candidate.slot === slot)) {
        const option = document.createElement("option");
        option.value = String(item.kind);
        option.textContent = item.name;
        option.selected = current.equipment[slot] === item.kind;
        sel.append(option);
      }
      if (slot !== "weapon" && current.equipment[slot] === null) sel.value = "";
      equipment[slot] = sel;
      const preview = document.createElement("div");
      preview.className = "loadout-item-preview";
      previews.set(slot, preview);
      card.append(this.bullField(SLOT_LABELS[slot], sel), preview);
      gear.append(card);
    }

    const selectedEquipment = (): Record<EquipSlot, number | null> => {
      const selected = {} as Record<EquipSlot, number | null>;
      for (const slot of EQUIP_SLOTS) {
        const value = equipment[slot]!.value;
        selected[slot] = value === "" ? null : Number(value);
      }
      return selected;
    };
    const refreshPreview = (): void => {
      const selected = selectedEquipment();
      const cls = classSel.value as PlaytestLoadoutWire["classKind"];
      // Draft only: the server validates Save. This calls the SAME shared
      // composition as heroStats, over the server's next-floor catalog.
      const stats = deriveGearCombatStats(catalog.items, selected, catalog.combat,
        catalog.classes.levels[level - 1]!, catalog.classes.defs[cls]);
      totals.replaceChildren(gearStatsPanel(stats));
      for (const slot of EQUIP_SLOTS) {
        const preview = previews.get(slot)!;
        preview.replaceChildren();
        const def = catalog.items.find((item) => item.kind === selected[slot]);
        if (!def) {
          const empty = document.createElement("div");
          empty.className = "loadout-empty";
          empty.textContent = "Empty slot";
          preview.append(empty);
          continue;
        }
        const im = document.createElement("img");
        im.className = "loadout-item-icon";
        im.alt = "";
        const url = this.heroAssets ? itemFrameUrl(this.heroAssets, def.icon, "canon") : null;
        im.src = url ?? `/sprites/items/${def.icon}/canon.png`;
        preview.append(im, itemStatsPanel(def, itemEfficiency(def, stats)));
      }
    };
    classSel.addEventListener("change", refreshPreview);
    for (const sel of Object.values(equipment)) sel.addEventListener("change", refreshPreview);
    refreshPreview();

    const row = document.createElement("div");
    row.className = "modal-actions";
    const save = document.createElement("button");
    save.type = "button";
    save.className = "modal-primary";
    save.textContent = "Save loadout";
    save.addEventListener("click", () => {
      const selected = selectedEquipment();
      save.disabled = true;
      this.net.playtestLoadout({
        classKind: classSel.value as PlaytestLoadoutWire["classKind"],
        level,
        equipment: selected,
      });
      // The confirmation is the server's own chat line (onPlaytestLoadout →
      // "The Bull has your next build ready.") — no bottom-left note: it
      // painted over the chat panel (the playtest's clipping report).
    });
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "modal-secondary";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => this.renderBullDialogue());
    row.append(save, cancel);
    editor.append(row);
    modal.body.replaceChildren(editor);
  }

  private bullField(labelText: string, control: HTMLSelectElement): HTMLLabelElement {
    const label = document.createElement("label");
    label.className = "modal-field";
    const text = document.createElement("span");
    text.textContent = labelText;
    label.append(text, control);
    return label;
  }

  // ---- the door (hub.md: confirm modal → OpenDoor op) --------------------

  /** Open the confirm: floor type + size, then Enter. The modal STAYS open
   *  after Enter — there is no receipt on the door op; the answer is a floor
   *  Welcome (index.ts switches the scene and destroys this hub) or an Error
   *  (onError closes the modal). Walking stops: the hero stands at the
   *  threshold while the party decides.
   *
   *  With NO party the confirm opens in its other face (A1): the reason and
   *  the way out, because "the door needs a party" is a fact the player can
   *  act on from exactly where they are standing. */
  private openDoorPanel(): void {
    this.stopWalk();
    this.viewport.closeMenu();
    this.closeBullPanel();
    if (this.doorModal?.backdrop.isConnected) return;
    this.closeDoorPanel(); // discard a modal closed through its own chrome
    this.buildDoorPanel();
  }

  private closeDoorPanel(): void {
    const modal = this.doorModal;
    this.doorModal = null;
    modal?.close();
    this.doorFloorSel = null;
    this.doorSizeSel = null;
    this.doorEnterBtn = null;
    this.doorNeedsParty = null;
  }

  /** A party landing (or leaving), or a race arming (or dying), while the confirm
   *  is open re-renders it: the "create a party" face becomes the picker the moment
   *  PartyCreate comes back on the wire, and the picker becomes the race question the
   *  moment a challenge arms — so the player never clicks a door the server is about
   *  to refuse, and never has to click the door twice. Cheap by construction: it only
   *  rebuilds when the state the modal was built FOR differs from the state now
   *  (traps/dom-rebuild.md: the roster rebroadcasts ~10/s in a busy town). */
  private refreshDoorPanel(): void {
    if (!this.doorModal?.backdrop.isConnected) return;
    const want = this.doorFace();
    if (want === this.doorFaceNow) return;
    this.closeDoorPanel();
    this.buildDoorPanel();
  }

  /** The face the door should be showing right now. */
  private doorFace(): "needs-party" | "picker" | "race" {
    if (this.myCommittedRace()) return "race";
    return this.myParty() === null ? "needs-party" : "picker";
  }

  /**
   * The race face of the door: one question, two verbs, the locked terms shown as
   * state rather than as a paragraph (11-15).
   *
   * There is deliberately NO forfeit button here. There is no wire verb for a
   * client-asserted forfeit, and adding one would hand a client a claim about the
   * other side's dungeon (C3). The way out is the way out of any floor: walk back up
   * through it, and the server observes what that means.
   */
  private buildRaceDoorPanel(race: RaceWire, side: 0 | 1): void {
    const modal = createGameModal(this.dom, "Ready to start the keyrace?", "enter-dungeon");
    this.doorModal = modal;
    const floor = FLOORS.find((f) => f.id === race.floor)?.name ?? `floor ${race.floor}`;
    const copy = document.createElement("p");
    copy.className = "modal-copy";
    const yes = document.createElement("button");
    yes.type = "button";
    yes.className = "modal-primary";
    if (race.sides[side].started) {
      // Their side is already down there — a player back through the return portal,
      // or a reconnect. One attempt per side is the shape of a race, so the door says
      // so instead of offering a second attempt at the same time.
      copy.textContent = `Your side is already in its race floor. ${floor} · ${race.size} · ${race.rated ? "rated" : "unrated"}.`;
      yes.textContent = "Close";
      yes.addEventListener("click", () => this.closeDoorPanel());
    } else {
      copy.textContent = `${floor} · ${race.size} · ${race.rated ? "rated" : "unrated"} · against ${this.sideName(race, (1 - side) as 0 | 1)}.`;
      yes.textContent = "Yes";
      yes.addEventListener("click", () => {
        yes.disabled = true; // one door per race; the answer is the floor arriving
        this.chat.system("Opening the race door…");
        this.net.raceBegin(race.id);
        this.closeDoorPanel();
      });
    }
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "modal-secondary";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => this.closeDoorPanel());
    const row = document.createElement("div");
    row.className = "modal-actions";
    row.append(yes, cancel);
    modal.body.append(copy, row);
  }

  private buildDoorPanel(): void {
    // A party in an armed race gets a different door (keyracing.md C2/D2). The
    // ordinary picker would be a lie: the server refuses an armed side's ordinary
    // floor, because a side that went off to play something else would leave a race
    // waiting in a dungeon for nobody. And the race door asks ONE question — the
    // terms were locked when the race armed, so a floor picker here would offer a
    // choice that is already made.
    const committed = this.myCommittedRace();
    if (committed) {
      this.doorFaceNow = "race";
      this.buildRaceDoorPanel(committed.race, committed.side);
      return;
    }
    if (this.myParty() === null) {
      this.buildDoorNeedsPartyPanel();
      return;
    }
    this.doorNeedsParty = false;
    this.doorFaceNow = "picker";
    const modal = createGameModal(this.dom, "Enter the dungeon", "enter-dungeon");
    this.doorModal = modal;
    const body = modal.body;
    const hint = document.createElement("div");
    hint.className = "modal-hint";
    hint.textContent = "Choose your floor and size. No one knows the layout until the door opens.";
    body.append(hint);

    // Floor type: floors.store's committed projection (FLOORS). An empty
    // catalog is legal (the user authors floor types in the knobs app's
    // Floors tab) — the door then says so and stays shut.
    const floorSel = document.createElement("select");
    if (FLOORS.length === 0) {
      const opt = document.createElement("option");
      opt.value = "-1";
      opt.textContent = "no floor types yet (knobs app → Floors)";
      floorSel.appendChild(opt);
    } else {
      for (const f of FLOORS) {
        const opt = document.createElement("option");
        opt.value = String(f.id);
        opt.textContent = f.name;
        floorSel.appendChild(opt);
      }
    }
    this.doorFloorSel = floorSel;

    const sizeSel = document.createElement("select");
    for (const s of ["small", "medium", "large"] as const) {
      const opt = document.createElement("option");
      opt.value = s;
      opt.textContent = s[0]!.toUpperCase() + s.slice(1);
      sizeSel.appendChild(opt);
    }
    this.doorSizeSel = sizeSel;

    const mkLabel = (text: string, sel: HTMLSelectElement): HTMLLabelElement => {
      const label = document.createElement("label");
      const span = document.createElement("span");
      span.textContent = text;
      label.append(span, sel);
      return label;
    };

    const enter = document.createElement("button");
    enter.type = "button";
    enter.className = "modal-primary";
    enter.textContent = "Enter";
    if (FLOORS.length === 0) enter.disabled = true;
    enter.addEventListener("click", () => this.enterDungeon());
    this.doorEnterBtn = enter;

    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "modal-secondary";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => this.closeDoorPanel());

    const row = document.createElement("div");
    row.className = "modal-actions";
    row.append(enter, cancel);
    body.append(
      mkLabel("Floor type", floorSel),
      mkLabel("Size", sizeSel),
      row,
    );
  }

  /** The door's other face (keyracing.md A1): the door requires a party, and
   *  this is the reason plus the one verb that fixes it. Founding a party of
   *  one is exactly what a solo used to get for free by clicking — it is now
   *  one explicit step, and the confirm flips itself into the picker when the
   *  server's roster says the party exists (refreshDoorPanel). */
  private buildDoorNeedsPartyPanel(): void {
    this.doorNeedsParty = true;
    this.doorFaceNow = "needs-party";
    const modal = createGameModal(this.dom, "The door is locked", "enter-dungeon");
    this.doorModal = modal;
    const copy = document.createElement("p");
    copy.className = "modal-copy";
    copy.textContent =
      "The door answers to a party, not to a person. Found one — a party of one is enough to go down alone.";
    const create = document.createElement("button");
    create.type = "button";
    create.className = "modal-primary";
    create.textContent = "Create party";
    create.addEventListener("click", () => {
      create.disabled = true; // one create per click; the answer is the roster
      this.net.partyCreate();
    });
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "modal-secondary";
    cancel.textContent = "Not now";
    cancel.addEventListener("click", () => this.closeDoorPanel());
    const row = document.createElement("div");
    row.className = "modal-actions";
    row.append(create, cancel);
    modal.body.append(copy, row);
  }

  /** The modal's Enter: the OpenDoor op (pre-game, no seq — the server
   *  stamps nothing, it just opens). Enter is disabled until the floor
   *  Welcome lands (there is no ack to wait for) or an error re-enables it. */
  private enterDungeon(): void {
    const floorId = Number(this.doorFloorSel?.value ?? "-1");
    const size = (this.doorSizeSel?.value ?? "small") as "small" | "medium" | "large";
    if (!Number.isInteger(floorId) || floorId < 0) return;
    if (this.doorEnterBtn) this.doorEnterBtn.disabled = true;
    // Chat-only feedback (the old bottom-left note clipped over the chat
    // panel — the playtest's report): the door answer is the floor Welcome
    // or an Error line, both in the chat already.
    this.chat.system("Opening the door…");
    this.net.openDoor(floorId, size);
  }

  // ---- chrome -------------------------------------------------------------

  private buildDom(): HTMLDivElement {
    this.root.textContent = "";
    const hub = document.createElement("div");
    hub.id = "hub";
    this.canvas = document.createElement("canvas");
    hub.appendChild(this.canvas);

    this.hud = new Hud(hub);
    this.hud.renderAbilities(hubAbilities());
    this.hud.renderEquipment(EQUIP_SLOTS.map((slot) => ({ slot, label: SLOT_LABELS[slot], itemKind: null })));
    this.hud.renderInventory([]);

    // One bar: identity + count on the left, title + sign out on the
    // right (the hover readout owns the top-LEFT corner — the Viewport
    // parks it there, like in the floor).
    const top = document.createElement("div");
    top.id = "hub-top";
    const row = document.createElement("div");
    row.className = "row";
    const who = document.createElement("span");
    who.className = "hint";
    who.id = "hub-account";
    who.textContent = `signed in as ${getAuthUsername() ?? "player"}`;
    this.countEl = document.createElement("span");
    this.countEl.className = "hint";
    this.countEl.id = "hub-count";
    this.partyEl = document.createElement("span");
    this.partyEl.className = "hint";
    this.partyEl.id = "hub-party";
    this.partyEl.hidden = true;
    const h1 = document.createElement("h1");
    h1.textContent = "THE HUB";
    const out = document.createElement("button");
    out.textContent = "sign out";
    out.className = "linkish";
    out.addEventListener("click", () => this.callbacks.onSignOut());
    // The pending invites are NOT in the top bar any more (keyracing.md A3):
    // a small caption with two underlined words read as a status line, not a
    // decision. They own a centred accept/reject window now (updateInviteWindow).
    const records = document.createElement("a");
    records.id = "hub-records";
    records.href = "/leaderboard";
    records.dataset.records = "";
    records.className = "linkish";
    records.textContent = "The boards";
    row.append(who, this.countEl, this.partyEl, h1, records, out);
    top.appendChild(row);

    // The town's chat (hub.md): the floor's ChatPanel, one line per
    // ServerOp.HubChat — the server is the broadcaster, the scene only
    // renders (AGENTS §6).
    this.chat = new ChatPanel(hub);
    this.chat.onSend = (text) => this.net.hubChat(text);

    hub.append(top);
    this.root.appendChild(hub);
    return hub;
  }

}
